"use client";

import React, { useState } from "react";
import { alpha, Avatar, Box, Typography, styled } from "@mui/material";
import toast from "react-hot-toast";
import {
  RightColumn,
  SectionHeader,
  SectionContent,
  Label,
} from "./styles/SharedStyles";
import EditButton from "@components/shared/EditButton";
import ReorderButtons from "@components/shared/ReorderButtons";
import MarkdownList from "@components/shared/MarkdownList";
import EditEducation from "../modals/EditEducation";
import { ProfileSkeleton } from "./skeletons";
import {
  useAddCandidateEducation,
  useUpdateCandidateEducation,
  useRemoveCandidateEducation,
  useReorderCandidateEducation,
} from "@lib/candidates/useEducationMutations";

const EducationWrapper = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  paddingBottom: theme.spacing(3),
  borderBottom: `1px solid ${theme.palette.divider}`,
  display: "flex",
  position: "relative",

  "&.no-border": {
    borderBottom: "none",
    marginBottom: 0,
    paddingBottom: 0,
  },
}));

const EducationDetails = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(2),
  width: "100%",
}));

const EducationProperties = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
  width: "100%",
}));

const EducationImage = styled(Avatar, {
  shouldForwardProp: (prop) => prop !== "themeColor",
})(({ theme, themeColor }) => ({
  width: "32px",
  height: "32px",
  borderRadius: "50%",
  overflow: "hidden",
  alignSelf: "flex-start",
  backgroundColor: themeColor
    ? alpha(themeColor, 0.4)
    : theme.palette.primary.lighter,
  fontSize: "12px",
  fontWeight: "600",
  color: theme.palette.black?.main || theme.palette.text.primary,
}));

const EducationTitle = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "8px",
  fontSize: 14,
  marginBottom: "8px",
  paddingTop: "7px",
  width: "85%",
  flex: 1,

  "& p": {
    margin: 0,
    lineHeight: 1.2,
    overflowWrap: "anywhere",
  },

  // The reorder/edit buttons are absolutely positioned over the top-right of
  // the row, so keep the first line clear of them (matters in the sidebar).
  "& p:first-of-type": {
    paddingRight: "160px",
  },
});

const EducationDescription = styled(Typography)(({ theme }) => ({
  lineHeight: 1.6,
}));

const AddEducationWrapper = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(1),
  width: "100%",
  height: "50px",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  border: `1px dashed ${theme.palette.divider}`,
  borderRadius: "4px",
  cursor: "pointer",

  "&:hover": {
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.grey[100],
  },

  "& span": {
    color: theme.palette.secondary.main,
  },
}));

const Education = ({ candidate, isImporting }) => {
  const [editingEducation, setEditingEducation] = useState(null);

  // Mutations
  const addEducation = useAddCandidateEducation();
  const updateEducation = useUpdateCandidateEducation();
  const removeEducation = useRemoveCandidateEducation();
  const reorderEducation = useReorderCandidateEducation();

  const handleReorder = (index, direction) => {
    const educationItems = [...candidate.education];
    const newIndex = direction === "up" ? index - 1 : index + 1;
    [educationItems[index], educationItems[newIndex]] = [educationItems[newIndex], educationItems[index]];
    const toastId = toast.loading("Saving changes...");
    reorderEducation.mutate(
      {
        candidate,
        order: educationItems.map((e) => e.id),
      },
      {
        onSuccess: () => {
          toast.dismiss(toastId);
        },
        onError: () => {
          toast.dismiss(toastId);
          toast.error("Failed to reorder education");
        },
      }
    );
  };

  const handleEditEducation = (education) => {
    setEditingEducation(education);
  };

  const handleAddEducation = (event) => {
    const toastId = toast.loading("Adding...");
    event.preventDefault();
    addEducation.mutate(
      {
        candidate,
        education: {
          course_name: "New education",
          institution: "Institution",
          year_completed: "",
          description: "",
        },
      },
      {
        onSuccess: (data) => {
          toast.dismiss(toastId);
          setEditingEducation(data.data.education);
        },
        onError: () => {
          toast.dismiss(toastId);
          toast.error("Failed to add education");
        },
      }
    );
  };

  const handleUpdateEducation = (updatedEducation) => {
    const toastId = toast.loading("Saving...");
    updateEducation.mutate(
      { candidate, education: updatedEducation },
      {
        onSuccess: () => {
          toast.dismiss(toastId);
        },
        onError: () => {
          toast.dismiss(toastId);
          toast.error("Failed to save");
        },
      }
    );
  };

  const handleDeleteEducation = (educationId) => {
    const toastId = toast.loading("Removing education...");
    removeEducation.mutate(
      { candidate, educationId },
      {
        onSuccess: () => {
          toast.dismiss(toastId);
        },
        onError: () => {
          toast.dismiss(toastId);
          toast.error("Failed to remove education");
        },
      }
    );
  };

  return (
    <Box>
      <SectionContent>
        <Box>
          <RightColumn hideBorder={true}>
            <SectionHeader>
              <Typography variant="h2">Education</Typography>
            </SectionHeader>
            {candidate.education?.map((edu, index) => (
              <EducationWrapper
                key={edu.id}
                className={
                  index === candidate.education.length - 1 && !isImporting
                    ? "no-border"
                    : ""
                }
              >
                <ReorderButtons
                  onMoveUp={index > 0 ? () => handleReorder(index, "up") : null}
                  onMoveDown={
                    index < candidate.education.length - 1
                      ? () => handleReorder(index, "down")
                      : null
                  }
                  sx={{ position: "absolute", top: "-8px", right: 96, height: 36 }}
                />
                <EditButton
                  onEdit={() => handleEditEducation(edu)}
                  sx={{ top: 0, right: 0 }}
                />
                <Label sx={{ lineHeight: 2 }}>Education details:</Label>
                <EducationDetails>
                  <EducationImage src={edu.logo} alt={edu.course_name}>
                    {edu.institution?.substring(0, 2) || "Ed"}
                  </EducationImage>
                  <EducationProperties>
                    <EducationTitle>
                      <p>{edu.course_name}</p>
                      <p>{edu.institution}</p>
                      <p>{edu.year_completed}</p>
                    </EducationTitle>
                    <EducationDescription>
                      <MarkdownList content={edu.description} />
                    </EducationDescription>
                  </EducationProperties>
                </EducationDetails>
              </EducationWrapper>
            ))}
            {isImporting && (
              <EducationWrapper className="no-border">
                <Label sx={{ lineHeight: 2 }}>Education details:</Label>
                <ProfileSkeleton />
              </EducationWrapper>
            )}
            <AddEducationWrapper onClick={handleAddEducation}>
              <span>Add new education +</span>
            </AddEducationWrapper>
          </RightColumn>
        </Box>
      </SectionContent>
      <EditEducation
        key={editingEducation?.id}
        open={editingEducation !== null}
        onClose={() => setEditingEducation(null)}
        education={editingEducation}
        onUpdate={handleUpdateEducation}
        onDelete={handleDeleteEducation}
        candidate={candidate}
      />
    </Box>
  );
};

export default Education;
